import React, { useRef, useState, useEffect } from 'react'
import { useIntl } from 'react-intl'
import { ProductHeader, VariantProps, Icons, AlertBox } from 'msp-components'
import { useWindowSize, useBasket } from 'msp-integrations'
import {
  sealIconsMapper,
  benefitIconsMapper,
  getProductCategoryTag
} from '../../../utils/product.utils'
import JasminCommerceBox from '../JasminCommerceBox/JasminCommerceBox'
import { MIN_BOXES_ORDER, MIN_BOXES_FREE_DELIVERY } from '@/constants'
import { twMerge } from 'tailwind-merge'

const JasminProductHeader = ({
  product,
  variant,
  onVariantChange
}: {
  product: any
  variant?: VariantProps
  onVariantChange?: (productId: string) => void
}) => {
  const { formatMessage } = useIntl()
  const alertBoxRef = useRef<AlertBox>()
  const [alertBoxMessage, setAlertBoxMessage] = useState({
    type: 'error',
    message: ''
  })
  const { width, breakpoints } = useWindowSize()
  const checkDesktop = () => width && width >= breakpoints.lg
  const [isDesktop, setIsDesktop] = useState(checkDesktop)
  const { c_minBoxesFreeDelivery, c_minBoxesOrder } = product
  const minBoxesFreeDelivery = c_minBoxesFreeDelivery ?? MIN_BOXES_FREE_DELIVERY
  const minBoxesOrder = c_minBoxesOrder ?? MIN_BOXES_ORDER

  useEffect(() => {
    if (checkDesktop() !== isDesktop) {
      setIsDesktop(checkDesktop())
    }
  }, [width])

  const ProductHeaderStyles = {
    container:
      'hide-scrollbar h-fit w-full pt-12 lg:pt-12 lg:px-0 mb-0 mx-auto flex flex-col font-Roboto overflow-scroll',
    wrapper: 'relative mx-auto w-10/12 lg:w-96',
    tag: 'table text-14 leading-normal tracking-normal bg-black text-white font-Roboto py-2 px-3 max-w-fit mb-3 md:mb-5',
    preline:
      'table text-14 leading-[14px] tracking-0 uppercase font-bold font-Roboto text-white bg-black py-1 px-4 w-fit',
    productName:
      'text-36 font-Lora font-bold uppercase leading-44 tracking-0 mb-8 mt-4',
    shortDescription:
      'text-[15px] leading-24 tracking-0 mb-3 font-Roboto font-medium',
    longDescription:
      'text-[15px] leading-24 tracking-0 break-words hyphens-auto font-Roboto font-light relative mb-5 text-justify',
    sealsContainer:
      'flex justify-end md:absolute md:top-0 md:right-0 md:translate-x-full md:flex-col md:pl-4 md:justify-center',
    category:
      'table text-18 leading-[14px] tracking-0 uppercase font-bold font-Roboto w-fit underline pb-2',
    attributeIcons: '',
    attributeKeyClasses: 'font-Roboto font-bold'
  }

  const attributesClasses = 'text-10 font-Roboto font-medium'
  const seals = sealIconsMapper(product?.c_seal)
  const benefits = benefitIconsMapper(product?.c_benefits)
  const attributes = [
    {
      name: formatMessage({
        id: 'msp.product.attributes.intensity',
        defaultMessage: 'Intensity'
      }),
      value: product?.c_flavorIntensity
    },
    {
      name: formatMessage({
        id: 'msp.product.attributes.flavor',
        defaultMessage: 'Flavor'
      }),
      value: product?.c_flavour && (
        <div className={attributesClasses}>
          {product?.c_flavour?.join(', ')}
        </div>
      )
    },
    {
      name: formatMessage({
        id: 'msp.product.attributes.brew_time',
        defaultMessage: 'Brew time'
      }),
      value: product?.c_brew_time && (
        <div
          className={attributesClasses}
        >{`${product?.c_brew_time} Min / ${product?.c_brew_temp} °C`}</div>
      )
    }
  ]

  const messagesShipping = {
    minimumOrderQuantityMessage: formatMessage({
      id: 'msp.cart.shipment.minimum.order.value.term',
      defaultMessage: 'minimum order quantity'
    }),
    teaBoxMessage: formatMessage({
      id: 'msp.cart.shipment.brewbox.multiple',
      defaultMessage: 'Tea boxes'
    }),
    freeShippingMessage: formatMessage({
      id: 'msp.cart.shipment.minimum.free.shipping.term',
      defaultMessage: 'Free shipping'
    }),
    fromMessage: formatMessage({
      id: 'msp.cart.shipment.free.shipping.from',
      defaultMessage: 'From'
    })
  }

  const InfoMessage = ({
    boldMessage,
    message,
    value,
    from
  }: {
    boldMessage: string
    message: string
    value: number
    from?: string
  }) => (
    <div>
      <span className='font-bold'>{boldMessage}</span>
      <span>
        {from ? (
          ` ${from} ${value}`
        ) : (
          <>
            <span className='font-bold'>{`: `}</span> <span>{value}</span>
          </>
        )}{' '}
        {message}
      </span>
    </div>
  )

  const onShowAlert = (alert: { type: string; message: string }) => {
    setAlertBoxMessage(alert)
    alertBoxRef?.current?.showAlert()
  }

  return (
    <>
      <AlertBox
        ref={alertBoxRef}
        type={alertBoxMessage.type}
        title={alertBoxMessage.message}
        autoCloseTime={2000}
        position='bottom'
        showBackdrop={false}
      />
      <ProductHeader
        attributes={attributes}
        category={getProductCategoryTag(product, ProductHeaderStyles.category)}
        classification={product?.c_blend}
        longDescription={product?.longDescription}
        name={product?.name}
        promotionTag={product?.c_promotionMessage}
        seals={seals}
        shortDescription={product?.shortDescription}
        styling={{ ...ProductHeaderStyles }}
        filledIcon={<Icons iconName='DropFilled' width='20px' />}
        emptyIcon={<Icons iconName='DropEmpty' width='20px' />}
      />
      {product?.c_servings && (
        <div
          className={twMerge(
            'mt-4 flex justify-start',
            ProductHeaderStyles.wrapper,
            attributesClasses
          )}
        >
          {product?.c_servings}
        </div>
      )}
      {product?.c_benefits?.length > 0 && (
        <div
          className={twMerge(
            'font-Roboto mt-6 flex flex-col justify-start',
            ProductHeaderStyles.wrapper
          )}
        >
          <a
            href='#ingredients'
            className='text-10 tracking-3 mb-2 font-bold uppercase'
          >
            {formatMessage({
              id: 'msp.product.benefits.title',
              defaultMessage: 'Ingredients'
            })}
          </a>
          <div className='flex flex-row space-x-4'>
            {benefits.map((benefit, index) => (
              <div className='flex flex-col justify-items-center' key={index}>
                <div className='flex justify-center'>{benefit?.icon}</div>
                <div className='text-10 mt-2'>{benefit?.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className={twMerge('mx-auto mt-10', ProductHeaderStyles.wrapper)}>
        <JasminCommerceBox
          isPDP={true}
          data={{
            showAnchorLink: true,
            product: {
              ...product,
              variant
            },
            type: 'LG',
            addButtonAsIcon: isDesktop ? undefined : 'XS'
          }}
          onVariantChange={onVariantChange}
          noPadding
          onWishlistChange={onShowAlert}
          showLowestPrice
        />
      </div>
      {product.c_cartCategoryId === 'tea' &&
      (minBoxesOrder || minBoxesFreeDelivery) ? (
        <div className='bg-greige text-pineGreen text-14 font-Roboto tracking-1 mx-auto mt-9 flex w-10/12 flex-col gap-3 p-4 uppercase lg:w-96'>
          {minBoxesOrder ? (
            <InfoMessage
              boldMessage={messagesShipping.minimumOrderQuantityMessage}
              message={messagesShipping.teaBoxMessage}
              value={minBoxesOrder}
            />
          ) : null}
          {minBoxesFreeDelivery ? (
            <InfoMessage
              boldMessage={messagesShipping.freeShippingMessage}
              from={messagesShipping.fromMessage}
              message={messagesShipping.teaBoxMessage}
              value={minBoxesFreeDelivery}
            />
          ) : null}
        </div>
      ) : null}
    </>
  )
}

export default JasminProductHeader
